import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle, ArrowRight, Sparkles, TrendingUp } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { calculateEstimate, formatPrice } from '../utils/pricing';

export const InstantQuote = () => {
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [showEstimate, setShowEstimate] = useState(false);
  const [formData, setFormData] = useState({
    propertyType: '',
    projectType: '',
    specificType: '',
    bedrooms: '',
    postcode: '',
    name: '',
    email: '',
    phone: ''
  });

  const [estimate, setEstimate] = useState(null);

  // Recalculate estimate whenever form data changes
  useEffect(() => {
    if (formData.propertyType || formData.projectType) {
      const calc = calculateEstimate(formData);
      setEstimate(calc);
      if (formData.propertyType && formData.projectType) {
        setShowEstimate(true);
      }
    }
  }, [formData]);

  const propertyTypes = [
    { value: 'detached', label: 'Detached', icon: '🏠' },
    { value: 'semi-detached', label: 'Semi-Detached', icon: '🏘️' },
    { value: 'terraced', label: 'Terraced', icon: '🏚️' },
    { value: 'flat', label: 'Flat', icon: '🏢' },
    { value: 'bungalow', label: 'Bungalow', icon: '🏡' }
  ];

  const projectTypes = [
    { value: 'extension', label: 'Ground Floor', sublabel: 'Extension' },
    { value: 'loft', label: 'Loft', sublabel: 'Conversion' },
    { value: 'first-floor', label: 'First Floor', sublabel: 'Extension' },
    { value: 'basement', label: 'Basement', sublabel: 'Conversion' },
    { value: 'other', label: 'Other', sublabel: 'Projects' }
  ];

  const extensionTypes = [
    { value: 'rear', label: 'Rear', icon: '⬅️' },
    { value: 'side', label: 'Side', icon: '↔️' },
    { value: 'side-infill', label: 'Side Infill', icon: '◼️' },
    { value: 'wraparound', label: 'Wraparound', icon: '🔄' },
    { value: 'side-and-rear', label: 'Side and Rear', icon: '↙️' },
    { value: 'two-storey', label: 'Two Storey', icon: '⬆️' }
  ];

  const loftTypes = [
    { value: 'dormer', label: 'Main Dormer', icon: '🏠' },
    { value: 'outrigger', label: 'Outrigger Dormer', icon: '📐' },
    { value: 'hip-to-gable-main', label: 'Hip to Gable with Main Dormer', icon: '🏛️' },
    { value: 'hip-to-gable-no', label: 'Hip to Gable (No Dormer)', icon: '📏' },
    { value: 'l-shaped', label: 'L Shaped Dormer', icon: '📐' }
  ];

  const firstFloorTypes = [
    { value: 'side-and-rear', label: 'Side and Rear', icon: '↙️' },
    { value: 'rear', label: 'Rear', icon: '⬅️' },
    { value: 'rear-over-outrigger', label: 'Rear Over Outrigger', icon: '⬆️' },
    { value: 'rear-over-ground', label: 'Rear Over Ground Floor', icon: '⬆️' },
    { value: 'side', label: 'Side', icon: '↔️' }
  ];

  const basementTypes = [
    { value: 'full', label: 'Full Conversion', icon: '🏗️' },
    { value: 'partial', label: 'Partial Conversion', icon: '🔨' },
    { value: 'extension', label: 'Basement Extension', icon: '⬇️' }
  ];

  const otherTypes = [
    { value: 'garage-conversion', label: 'Garage Conversion', icon: '🚗' },
    { value: 'garden-annex', label: 'Garden Annex', icon: '🌳' },
    { value: 'two-storey-rear', label: 'Two Storey Rear', icon: '⬆️' },
    { value: 'two-storey-side', label: 'Two Storey Side', icon: '⬆️' }
  ];

  const handleSelect = (field, value) => {
    setFormData({ ...formData, [field]: value });
    
    // Auto-advance to next step after selection
    if (field === 'propertyType') {
      setTimeout(() => setCurrentStep(2), 300);
    } else if (field === 'projectType') {
      if (['extension', 'loft', 'first-floor', 'basement', 'other'].includes(value)) {
        setTimeout(() => setCurrentStep(3), 300);
      } else {
        setTimeout(() => setCurrentStep(4), 300);
      }
    } else if (field === 'specificType') {
      setTimeout(() => setCurrentStep(4), 300);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Submit the quote request to the backend
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads/architecture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          project_type: `${formData.projectType} - ${formData.specificType}`,
          property_address: `${formData.propertyType} property`,
          postcode: formData.postcode,
          budget_range: estimate ? `Design: ${formatPrice(estimate.designMin)} - ${formatPrice(estimate.designMax)}, Build: ${formatPrice(estimate.buildMin)} - ${formatPrice(estimate.buildMax)}` : 'Quote requested',
          project_description: `Property Type: ${formData.propertyType}, Project: ${formData.projectType}, Specific: ${formData.specificType}, Bedrooms: ${formData.bedrooms}`,
          preferred_contact: 'email'
        })
      });
      
      if (response.ok) {
        toast({
          title: "Quote Request Sent!",
          description: "We'll send your detailed quote to your email within 1 hour.",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to submit quote request. Please try again.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Quote submission error:', error);
      toast({
        title: "Error",
        description: "Failed to submit quote request. Please try again.",
        variant: "destructive"
      });
    }
  };

  const getSpecificTypes = () => {
    switch (formData.projectType) {
      case 'extension':
        return extensionTypes;
      case 'loft':
        return loftTypes;
      case 'first-floor':
        return firstFloorTypes;
      case 'basement':
        return basementTypes;
      case 'other':
        return otherTypes;
      default:
        return [];
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Sticky Price Display */}
      {showEstimate && estimate && (
        <div className="fixed top-20 right-0 left-0 z-40 bg-gradient-to-r from-[#FF8C42] to-[#FF6B35] text-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Sparkles className="w-6 h-6" />
                <div>
                  <div className="text-sm opacity-90">Your Estimated Design Cost</div>
                  <div className="text-2xl font-bold">
                    {formatPrice(estimate.designMin)} - {formatPrice(estimate.designMax)}
                  </div>
                </div>
              </div>
              <div className="hidden md:flex items-center space-x-6">
                <div>
                  <div className="text-xs opacity-90">Planning Application</div>
                  <div className="font-semibold">{formatPrice(estimate.planning)}</div>
                </div>
                <div className="border-l border-white/30 pl-6">
                  <div className="text-xs opacity-90">Total Project Cost</div>
                  <div className="text-xl font-bold">
                    {formatPrice(estimate.totalMin)} - {formatPrice(estimate.totalMax)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={`max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 ${showEstimate ? 'pt-32' : 'pt-24'} pb-16`}>
        {/* Header */}
        <div className="text-center mb-12 fade-in">
          <div className="inline-flex items-center space-x-2 bg-orange-50 px-4 py-2 rounded-full mb-4">
            <TrendingUp className="w-5 h-5 text-[#FF8C42]" />
            <span className="text-[#FF8C42] font-semibold">Calculate design costs in 30 seconds</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-[#404040] mb-4">
            Instant Price Estimate for H&J
          </h1>
          <p className="text-lg text-gray-600">
            London's trusted architectural practice with 95% planning success rate
          </p>
        </div>

        {/* Form Card */}
        <Card className="border-none shadow-2xl overflow-hidden">
          <CardContent className="p-0">
            <form onSubmit={handleSubmit}>
              {/* Step 1: Property Type */}
              {currentStep === 1 && (
                <div className="p-8 md:p-12 fade-in">
                  <h2 className="text-2xl font-bold text-[#404040] mb-8">
                    What type of property are you extending?
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {propertyTypes.map((type) => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => handleSelect('propertyType', type.value)}
                        className={`p-6 rounded-xl border-2 transition-all hover:scale-105 hover:shadow-lg ${
                          formData.propertyType === type.value
                            ? 'border-[#FF8C42] bg-orange-50 shadow-md'
                            : 'border-gray-200 hover:border-[#FF8C42]'
                        }`}
                      >
                        <div className="text-5xl mb-3">{type.icon}</div>
                        <div className="font-semibold text-[#404040] text-sm">{type.label}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 2: Project Type */}
              {currentStep === 2 && (
                <div className="p-8 md:p-12 fade-in">
                  <button
                    type="button"
                    onClick={() => setCurrentStep(1)}
                    className="text-[#FF8C42] hover:underline mb-6 text-sm font-medium"
                  >
                    ← Change property type
                  </button>
                  <h2 className="text-2xl font-bold text-[#404040] mb-8">
                    What do you want to extend?
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {projectTypes.map((type) => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => handleSelect('projectType', type.value)}
                        className={`p-6 rounded-xl border-2 transition-all hover:scale-105 hover:shadow-lg ${
                          formData.projectType === type.value
                            ? 'border-[#FF8C42] bg-orange-50 shadow-md'
                            : 'border-gray-200 hover:border-[#FF8C42]'
                        }`}
                      >
                        <div className="font-bold text-[#404040] mb-1">{type.label}</div>
                        <div className="text-sm text-gray-600">{type.sublabel}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 3: Specific Type */}
              {currentStep === 3 && (
                <div className="p-8 md:p-12 fade-in">
                  <button
                    type="button"
                    onClick={() => setCurrentStep(2)}
                    className="text-[#FF8C42] hover:underline mb-6 text-sm font-medium"
                  >
                    ← Change project type
                  </button>
                  <h2 className="text-2xl font-bold text-[#404040] mb-8">
                    What kind of <span className="text-[#FF8C42]">{formData.projectType}</span> extension?
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {getSpecificTypes().map((type) => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => handleSelect('specificType', type.value)}
                        className={`p-6 rounded-xl border-2 transition-all hover:scale-105 hover:shadow-lg ${
                          formData.specificType === type.value
                            ? 'border-[#FF8C42] bg-orange-50 shadow-md'
                            : 'border-gray-200 hover:border-[#FF8C42]'
                        }`}
                      >
                        <div className="text-4xl mb-3">{type.icon}</div>
                        <div className="font-semibold text-[#404040] text-sm">{type.label}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 4: Property Details */}
              {currentStep === 4 && (
                <div className="p-8 md:p-12 fade-in">
                  <button
                    type="button"
                    onClick={() => setCurrentStep(formData.specificType ? 3 : 2)}
                    className="text-[#FF8C42] hover:underline mb-6 text-sm font-medium"
                  >
                    ← Back
                  </button>
                  <h2 className="text-2xl font-bold text-[#404040] mb-8">
                    How many bedrooms are there?
                  </h2>
                  <div className="grid grid-cols-5 gap-4 mb-8">
                    {['1', '2', '3', '4', '5+'].map((num) => (
                      <button
                        key={num}
                        type="button"
                        onClick={() => handleSelect('bedrooms', num)}
                        className={`p-6 rounded-xl border-2 font-bold text-xl transition-all hover:scale-105 ${
                          formData.bedrooms === num
                            ? 'border-[#FF8C42] bg-orange-50 text-[#FF8C42] shadow-md'
                            : 'border-gray-200 hover:border-[#FF8C42]'
                        }`}
                      >
                        {num}
                      </button>
                    ))}
                  </div>
                  
                  {formData.bedrooms && (
                    <div className="space-y-6 fade-in">
                      <div>
                        <Label htmlFor="postcode" className="text-[#404040] font-medium mb-2 block">
                          Property Postcode
                        </Label>
                        <Input
                          id="postcode"
                          placeholder="e.g., SW1A 1AA"
                          value={formData.postcode}
                          onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                          className="text-lg"
                        />
                      </div>
                      
                      {formData.postcode && (
                        <Button
                          type="button"
                          onClick={() => setCurrentStep(5)}
                          className="w-full bg-[#FF8C42] hover:bg-[#FF6B35] text-white text-lg py-6 btn-hover-lift"
                        >
                          Continue to Contact Details
                          <ArrowRight className="w-5 h-5 ml-2" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Step 5: Contact Details */}
              {currentStep === 5 && (
                <div className="p-8 md:p-12 fade-in">
                  <button
                    type="button"
                    onClick={() => setCurrentStep(4)}
                    className="text-[#FF8C42] hover:underline mb-6 text-sm font-medium"
                  >
                    ← Back
                  </button>
                  <h2 className="text-2xl font-bold text-[#404040] mb-2">
                    Get Your Detailed Quote
                  </h2>
                  <p className="text-gray-600 mb-8">We'll email your personalized quote instantly</p>
                  
                  <div className="space-y-6">
                    <div>
                      <Label htmlFor="name" className="text-[#404040] font-medium mb-2 block">
                        Full Name *
                      </Label>
                      <Input
                        id="name"
                        placeholder="John Smith"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="text-lg"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-[#404040] font-medium mb-2 block">
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="john@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="text-lg"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-[#404040] font-medium mb-2 block">
                        Phone Number *
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="020 1234 5678"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        required
                        className="text-lg"
                      />
                    </div>
                    
                    <Button
                      type="submit"
                      className="w-full bg-[#FF8C42] hover:bg-[#FF6B35] text-white text-lg py-6 btn-hover-lift"
                    >
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Send Me My Quote
                    </Button>
                    
                    <p className="text-xs text-gray-500 text-center">
                      By submitting, you agree to be contacted by Harry & Jacobs about your project
                    </p>
                  </div>
                </div>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Trust Indicators */}
        <div className="grid grid-cols-3 gap-6 mt-12 text-center">
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-3xl font-bold text-[#FF8C42] mb-2">95%</div>
            <div className="text-sm text-gray-600">Planning Success</div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-3xl font-bold text-[#FF8C42] mb-2">500+</div>
            <div className="text-sm text-gray-600">Projects Completed</div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-3xl font-bold text-[#FF8C42] mb-2">4.8/5</div>
            <div className="text-sm text-gray-600">Client Rating</div>
          </div>
        </div>
      </div>
    </div>
  );
};
